import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertTriangle,
  ChevronRight,
  Info,
  Loader2,
  Upload,
} from 'lucide-react'
import { apiService } from '@/services/apiService'
import { cn } from '@/lib/utils'
import { useIncognito } from '@/hooks/use-incognito'
import { usePools } from '@/hooks/use-pools'
import { obfuscateUUID } from '@/lib/incognito'
import {
  PARALLEL_CHECK_SIZE,
  parseCSV,
  parseIDs,
  SummaryCard,
  FailureBreakdown,
} from './bulkSystemsShared'

type Step = 'import' | 'checking' | 'confirm' | 'executing' | 'done'
type OtherPoolBehavior = 'add' | 'remove' | 'skip'

interface SystemInfo {
  systemID: string
  otherPools: string[]
  alreadyInTargetPool: boolean
  touEnabled: boolean
  checkError?: string
}

interface Props {
  poolID: string
  poolPurpose?: string | null

  touFallback?: boolean
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function BulkAddSystemsDialog({ poolID, poolPurpose, touFallback = false, open, onClose, onSuccess }: Props) {
  const incognito = useIncognito()
  const { data: allPools = [] } = usePools()
  const isControlPoolId = (id: string) => !!allPools.find((p) => p.poolID === id)?.controlConfig?.purpose
  const isControlPool = !!poolPurpose
  const [step, setStep] = useState<Step>('import')
  const [inputText, setInputText] = useState('')
  const [parsedIDs, setParsedIDs] = useState<string[]>([])
  const [csvData, setCsvData] = useState<{ headers: string[]; rows: string[][] } | null>(null)
  const [checkProgress, setCheckProgress] = useState(0)
  const [systemInfos, setSystemInfos] = useState<SystemInfo[]>([])
  const [cumulative, setCumulative] = useState({ done: 0, skipped: 0, error: 0 })
  const [cumulativeFailures, setCumulativeFailures] = useState<Array<{ systemID: string; reason: string }>>([])
  const [cumulativeSuccesses, setCumulativeSuccesses] = useState<string[]>([])
  const [cumulativeSkippedIDs, setCumulativeSkippedIDs] = useState<string[]>([])
  const [activeFrom, setActiveFrom] = useState('')
  const [otherPoolBehavior, setOtherPoolBehavior] = useState<OtherPoolBehavior>('skip')
  const [disableTou, setDisableTou] = useState(false)
  const [isPreFiltering, setIsPreFiltering] = useState(false)
  const [simpleExecProgress, setSimpleExecProgress] = useState(0)
  const [detailExecProgress, setDetailExecProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cancelledRef = useRef(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const isSimpleMode = otherPoolBehavior === 'skip' && !disableTou && isControlPool
  const needsPhaseLimitsNotice =
    poolPurpose === 'imbalance' || poolPurpose === 'tou_stacking_poc'

  const handleTextChange = (text: string) => {
    setInputText(text)
    setParsedIDs(parseIDs(text))
  }

  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0)

  const processFile = (file: File) => {
    file.text().then((text) => {
      const parsed = parseCSV(text)
      if (parsed) {
        setCsvData(parsed)
      } else {
        setInputText(text)
        setParsedIDs(parseIDs(text))
      }
    })
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer.types.includes('Files')) {
      dragCounterRef.current++
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) setIsDragOver(false)
  }

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault() }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current = 0
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) processFile(file)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    processFile(file)
    e.target.value = ''
  }

  const startChecking = async () => {
    cancelledRef.current = false
    setStep('checking')
    setCheckProgress(0)

    // Fetch existing members of this pool upfront — systems already in it are marked without further API calls
    setIsPreFiltering(true)
    const poolSystemsResp = await apiService.systems.getSystemIds(poolID, true)
    setIsPreFiltering(false)
    if (cancelledRef.current) return
    const existingSet = new Set(
      poolSystemsResp.success && poolSystemsResp.data
        ? (poolSystemsResp.data as string[]).map((id: string) => id.toLowerCase())
        : []
    )

    // Split immediately: IDs already in this pool need no further API calls
    const alreadyInPool = parsedIDs.filter((id) => existingSet.has(id))
    const toCheck = parsedIDs.filter((id) => !existingSet.has(id))

    const alreadyInPoolInfos: SystemInfo[] = alreadyInPool.map((systemID) => ({
      systemID, otherPools: [], alreadyInTargetPool: true, touEnabled: false,
    }))



    const checkSystem = async (systemID: string): Promise<SystemInfo> => {
      try {
        const poolsResp = await apiService.systems.getPoolsBySystem(systemID)
        const allPools: string[] = poolsResp.success ? (poolsResp.data ?? []) : []
        const alreadyInTargetPool = allPools.includes(poolID)
        const otherPools = allPools.filter((p) => p !== poolID)

        let touEnabled = false
        if (!alreadyInTargetPool && (isControlPool ? disableTou : true) && !(otherPoolBehavior === 'skip' && otherPools.length > 0)) {
          const touResp = await apiService.tou.getSettings(systemID)
          if (!touResp.success && touResp.errorCode === 'NOT_FOUND') {
            const existsResp = await apiService.systems.get(systemID)
            if (!existsResp.success && existsResp.errorCode === 'NOT_FOUND') {
              return { systemID, otherPools: [], alreadyInTargetPool: false, touEnabled: false, checkError: 'System does not exist' }
            }
            // System exists but has no ToU settings — proceed with touEnabled = false
          } else if (touResp.success && touResp.data != null) {
            touEnabled = (touResp.data as any).enabled ?? false
          }
        }

        return { systemID, otherPools, alreadyInTargetPool, touEnabled }
      } catch {
        return { systemID, otherPools: [], alreadyInTargetPool: false, touEnabled: false, checkError: 'Check failed' }
      }
    }

    // Animate progress through already-in-pool IDs before starting network checks
    const TICK = 30 // ms between ticks
    const BATCH = Math.max(1, Math.ceil(alreadyInPool.length / 40))
    for (let n = 0; n < alreadyInPool.length; n += BATCH) {
      if (cancelledRef.current) return
      setCheckProgress(Math.min(n + BATCH, alreadyInPool.length))
      await new Promise((r) => setTimeout(r, TICK))
    }
    const checkedInfos: SystemInfo[] = []

    // Check systems concurrently in batches — far faster than one-by-one for large inputs
    for (let i = 0; i < toCheck.length; i += PARALLEL_CHECK_SIZE) {
      if (cancelledRef.current) return
      const chunk = toCheck.slice(i, i + PARALLEL_CHECK_SIZE)
      const results = await Promise.all(chunk.map(checkSystem))
      checkedInfos.push(...results)
      setCheckProgress(alreadyInPool.length + checkedInfos.length)
    }

    const infos = [...alreadyInPoolInfos, ...checkedInfos]

    setSystemInfos(infos)
    setStep('confirm')
  }

  const startExecuting = async () => {
    cancelledRef.current = false
    const activeFromISO = activeFrom ? new Date(activeFrom).toISOString() : undefined

    if (isSimpleMode) {
      setSimpleExecProgress(0)
      setStep('executing')
      let done = 0, skipped = 0, error = 0
      const failures: Array<{ systemID: string; reason: string }> = []
      const successes: string[] = []
      const skippedIDs: string[] = []

      for (let i = 0; i < systemInfos.length; i += PARALLEL_CHECK_SIZE) {
        if (cancelledRef.current) return
        const chunk = systemInfos.slice(i, i + PARALLEL_CHECK_SIZE)
        await Promise.all(chunk.map(async (info) => {
          if (info.alreadyInTargetPool || info.otherPools.length > 0) {
            skipped++
            skippedIDs.push(info.systemID)
            return
          }
          const resp = await apiService.systems.add(poolID, info.systemID, activeFromISO)
          if (resp.success) {
            done++
            successes.push(info.systemID)
          } else {
            error++
            failures.push({ systemID: info.systemID, reason: resp.error || 'Failed to add to pool' })
          }
        }))
        setSimpleExecProgress(i + chunk.length)
      }

      setCumulative((prev) => ({ done: prev.done + done, skipped: prev.skipped + skipped, error: prev.error + error }))
      if (failures.length > 0) setCumulativeFailures((prev) => [...prev, ...failures])
      if (successes.length > 0) setCumulativeSuccesses((prev) => [...prev, ...successes])
      if (skippedIDs.length > 0) setCumulativeSkippedIDs((prev) => [...prev, ...skippedIDs])
      setStep('done')
      return
    }

    setStep('executing')
    setDetailExecProgress(0)

    let batchDone = 0
    let batchSkipped = 0
    let batchError = 0
    const batchFailures: Array<{ systemID: string; reason: string }> = []
    const batchSuccesses: string[] = []
    const batchSkippedIDs: string[] = []

    type ExecOutcome = 'done' | 'skipped' | 'error'

    // Execute one system's remove → disable ToU → add sequence. Systems are independent,
    // so these can run concurrently. Progress is reported per batch via detailExecProgress.
    const executeSystem = async (
      info: SystemInfo
    ): Promise<{ outcome: ExecOutcome; systemID: string; reason?: string }> => {
      if (info.alreadyInTargetPool) {
        return { outcome: 'skipped', systemID: info.systemID }
      }
      if (otherPoolBehavior === 'skip' && info.otherPools.length > 0) {
        return { outcome: 'skipped', systemID: info.systemID }
      }

      // Remove from other control pools (only when otherPoolBehavior === 'remove')
      for (const pool of otherPoolBehavior === 'remove' ? info.otherPools.filter(isControlPoolId) : []) {
        const resp = await apiService.systems.remove(pool, info.systemID)
        if (!resp.success) {
          return {
            outcome: 'error',
            systemID: info.systemID,
            reason: resp.error || `Failed to remove from pool ${pool.slice(0, 8)}…`,
          }
        }
      }

      // Disable ToU (best-effort — failure doesn't block the add; skipped when disableTou is false)
      if (disableTou && info.touEnabled) {
        await apiService.tou.disable(info.systemID)
      }

      // Add to target pool
      const resp = await apiService.systems.add(poolID, info.systemID, activeFromISO)
      if (resp.success) {
        return { outcome: 'done', systemID: info.systemID }
      }
      return { outcome: 'error', systemID: info.systemID, reason: resp.error || 'Failed to add to pool' }
    }

    // Run systems concurrently in batches — far faster than one-by-one for large inputs
    for (let i = 0; i < systemInfos.length; i += PARALLEL_CHECK_SIZE) {
      if (cancelledRef.current) return
      const chunk = systemInfos.slice(i, i + PARALLEL_CHECK_SIZE)
      const results = await Promise.all(chunk.map((info) => executeSystem(info)))
      for (const r of results) {
        if (r.outcome === 'done') {
          batchDone++
          batchSuccesses.push(r.systemID)
        } else if (r.outcome === 'skipped') {
          batchSkipped++
          batchSkippedIDs.push(r.systemID)
        } else {
          batchError++
          batchFailures.push({ systemID: r.systemID, reason: r.reason || 'Failed to add to pool' })
        }
      }
      setDetailExecProgress(i + chunk.length)
    }

    setCumulative((prev) => ({
      done: prev.done + batchDone,
      skipped: prev.skipped + batchSkipped,
      error: prev.error + batchError,
    }))
    if (batchFailures.length > 0) setCumulativeFailures((prev) => [...prev, ...batchFailures])
    if (batchSuccesses.length > 0) setCumulativeSuccesses((prev) => [...prev, ...batchSuccesses])
    if (batchSkippedIDs.length > 0) setCumulativeSkippedIDs((prev) => [...prev, ...batchSkippedIDs])
    setStep('done')
  }

  const reset = () => {
    cancelledRef.current = true
    setStep('import')
    setInputText('')
    setParsedIDs([])
    setCsvData(null)
    setCheckProgress(0)
    setSystemInfos([])
    setCumulative({ done: 0, skipped: 0, error: 0 })
    setCumulativeFailures([])
    setCumulativeSuccesses([])
    setCumulativeSkippedIDs([])
    setActiveFrom('')
    setOtherPoolBehavior('skip')
    setDisableTou(false)
    setIsPreFiltering(false)
    setSimpleExecProgress(0)
    setDetailExecProgress(0)
  }

  const handleClose = () => {
    if (step === 'done' && cumulative.done > 0) onSuccess()
    reset()
    onClose()
  }

  const skipDueToOtherPool = otherPoolBehavior === 'skip'
    ? systemInfos.filter((s) => !s.alreadyInTargetPool && s.otherPools.length > 0)
    : []
  const toAdd = systemInfos.filter(
    (s) => !s.alreadyInTargetPool && !(otherPoolBehavior === 'skip' && s.otherPools.length > 0)
  )
  const toSkip = systemInfos.filter((s) => s.alreadyInTargetPool)
  const toMove = otherPoolBehavior === 'remove'
    ? systemInfos.filter((s) => !s.alreadyInTargetPool && s.otherPools.some(isControlPoolId))
    : []
  const toDisableTou = systemInfos.filter((s) => !s.alreadyInTargetPool && s.touEnabled)

  const isExecuting = step === 'executing'

  const description = (() => {
    switch (step) {
      case 'import':
        return 'Upload a CSV or paste system IDs to add them to this pool.'
      case 'checking':
        return 'Checking systems…'
      case 'confirm':
        return 'Review what will happen before proceeding.'
      case 'executing':
        return 'Processing systems — please wait.'
      case 'done':
        return 'All operations complete.'
    }
  })()

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && !isExecuting && handleClose()}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Bulk Add Systems to Pool</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 py-1">
          {step === 'import' && (
            <div className="space-y-3">
              {needsPhaseLimitsNotice && (
                <div className="flex items-start gap-2 rounded-md border bg-muted/50 p-3 text-sm text-muted-foreground">
                  <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>
                    The battery system must have <strong>phase limits configured</strong> when fuse
                    protection is active, or <strong>fuse protection must be disabled</strong>.
                  </span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={handleFileUpload}
              />
              {csvData ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Which column contains the System IDs?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {csvData.headers.map((header, colIdx) => {
                      const samples = csvData.rows
                        .map((row) => row[colIdx]?.trim() ?? '')
                        .filter(Boolean)
                        .slice(0, 3)
                      return (
                        <button
                          key={colIdx}
                          className="rounded-md border p-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                          onClick={() => {
                            const values = csvData.rows.map((row) => row[colIdx]?.trim() ?? '').join('\n')
                            const ids = parseIDs(values)
                            setInputText(ids.join('\n'))
                            setParsedIDs(ids)
                            setCsvData(null)
                          }}
                        >
                          <p className="text-sm font-semibold truncate">{header}</p>
                          {samples.map((s, i) => (
                            <p key={i} className="text-xs font-mono text-muted-foreground truncate mt-0.5">
                              {s}
                            </p>
                          ))}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
                    onClick={() => {
                      setCsvData(null)
                      setParsedIDs([])
                      fileInputRef.current?.click()
                    }}
                  >
                    ↑ Upload a different file
                  </button>
                </div>
              ) : (
                <>
                  <div
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    className="relative rounded-md transition-colors space-y-3"
                    style={{ border: `2px solid ${isDragOver ? 'var(--color-ring)' : 'transparent'}`, background: isDragOver ? 'color-mix(in oklab, var(--color-muted) 30%, transparent)' : undefined }}
                  >
                    {isDragOver && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md pointer-events-none">
                        <p className="text-sm font-medium text-foreground">Drop file to upload</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload CSV
                      </Button>
                      <span className="text-sm text-muted-foreground">or paste system IDs below</span>
                    </div>
                    <textarea
                      className="w-full h-40 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                      placeholder={`Paste system IDs here, one per line:\n550e8400-e29b-41d4-a716-446655440000\n123e4567-e89b-12d3-a456-426614174000`}
                      value={inputText}
                      onChange={(e) => handleTextChange(e.target.value)}
                    />
                    {(inputText || parsedIDs.length > 0) && (
                      <p className="text-sm text-muted-foreground">
                        {parsedIDs.length > 0 ? (
                          <span className="text-foreground font-medium">
                            {parsedIDs.length} unique system ID{parsedIDs.length !== 1 ? 's' : ''} found
                          </span>
                        ) : (
                          'No valid system IDs found'
                        )}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2 pt-1">
                    <div className="space-y-2">
                      <label htmlFor="bulkActiveFrom" className="text-sm font-medium">
                        Active From <span className="text-muted-foreground font-normal">(Optional)</span>
                      </label>
                      <input
                        id="bulkActiveFrom"
                        type="datetime-local"
                        value={activeFrom}
                        onChange={(e) => setActiveFrom(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <p className="text-xs text-muted-foreground">
                        When systems should start participating in this pool (your local time). Applied to all added systems. If not specified, defaults to now.
                      </p>
                    </div>
                    <div className="rounded-md border p-3 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Systems already in another pool</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Info className="h-4 w-4 flex-shrink-0" />
                        A system can only be part of one control pool. No restrictions on forecast pools.
                      </p>
                      {(['skip', 'add', 'remove'] as OtherPoolBehavior[]).map((value) => (
                        <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="radio"
                            name="otherPoolBehavior"
                            value={value}
                            checked={otherPoolBehavior === value}
                            onChange={() => setOtherPoolBehavior(value)}
                            className="h-4 w-4"
                          />
                          {value === 'add' && (
                            <span>
                              Add to this pool anyway
                              {isControlPool && (
                                <span className="block text-xs text-muted-foreground">
                                  Note: When the system is already part of a control pool, adding it to a new control pool will fail.
                                </span>
                              )}
                            </span>
                          )}
                          {value === 'skip' && (
                            <span>
                              Skip
                              <span className="block text-xs text-muted-foreground">
                                Don't add if already in another pool (control or forecast)
                              </span>
                            </span>
                          )}
                          {value === 'remove' && 'Remove from current control pool before adding'}
                        </label>
                      ))}
                    </div>
                    {isControlPool && (
                      <>
                        <label className={cn('flex items-center gap-2 text-sm', touFallback ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}>
                          <input
                            type="checkbox"
                            checked={disableTou}
                            onChange={(e) => setDisableTou(e.target.checked)}
                            disabled={touFallback}
                            className="h-4 w-4 rounded border-input"
                          />
                          Disable Time of Use when adding
                        </label>
                        {touFallback && (
                          <p className="text-xs text-muted-foreground pl-6">
                            Not available — this pool uses Time of Use as its fallback behavior.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {step === 'checking' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {isPreFiltering
                  ? 'Fetching pool membership…'
                  : `Checking system ${checkProgress} of ${parsedIDs.length}…`}
              </p>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${parsedIDs.length > 0 ? (checkProgress / parsedIDs.length) * 100 : 0}%`,
                  }}
                />
              </div>
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-4">

              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <SummaryCard label="To add" value={toAdd.length} colorClass="text-green-600" />
                <SummaryCard
                  label="Already in pool"
                  value={toSkip.length}
                  colorClass="text-muted-foreground"
                />
                {otherPoolBehavior === 'skip' && (
                  <SummaryCard label="To skip (other pool)" value={skipDueToOtherPool.length} colorClass="text-muted-foreground" />
                )}
                {otherPoolBehavior === 'remove' && (
                  <SummaryCard label="To move" value={toMove.length} colorClass="text-amber-600" />
                )}
                {disableTou && (
                  <SummaryCard
                    label="Disable ToU"
                    value={toDisableTou.length}
                    colorClass="text-blue-600"
                  />
                )}
              </div>

              {!isSimpleMode && <div className="rounded-md border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2 pl-3 font-medium">System ID</th>
                      <th className="text-left p-2 font-medium">Current pool(s)</th>
                      <th className="text-left p-2 font-medium">ToU</th>
                      <th className="text-left p-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemInfos.map((info) => (
                      <tr
                        key={info.systemID}
                        className="border-b last:border-0 hover:bg-muted/30"
                      >
                        <td className="p-2 pl-3 font-mono text-xs">
                          {(incognito.isIncognito ? obfuscateUUID(info.systemID) : info.systemID).slice(0, 8)}…
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">
                          {info.alreadyInTargetPool ? (
                            <span className="italic">this pool</span>
                          ) : info.otherPools.length > 0 ? (
                            info.otherPools.map((p) =>
                              `${(incognito.isIncognito ? obfuscateUUID(p) : p).slice(0, 8)}…`
                            ).join(', ')
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="p-2 text-xs">
                          {!info.alreadyInTargetPool && info.touEnabled ? (
                            <span className="text-amber-600">enabled</span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="p-2 text-xs">
                          {info.alreadyInTargetPool ? (
                            <span className="text-muted-foreground">skip</span>
                          ) : otherPoolBehavior === 'skip' && info.otherPools.length > 0 ? (
                            <span className="text-muted-foreground">skip</span>
                          ) : otherPoolBehavior === 'remove' && info.otherPools.some(isControlPoolId) ? (
                            <span className="text-amber-600">move</span>
                          ) : (
                            <span className="text-green-600">add</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}

              {otherPoolBehavior === 'remove' && toMove.length > 0 && (
                <p className="text-sm text-amber-600 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {toMove.length} system{toMove.length !== 1 ? 's' : ''} will be removed from
                  their current control pool before being added here.
                </p>
              )}
              {otherPoolBehavior === 'skip' && skipDueToOtherPool.length > 0 && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  {skipDueToOtherPool.length} system{skipDueToOtherPool.length !== 1 ? 's' : ''} will be skipped — already in another pool.
                </p>
              )}
              {activeFrom && (
                <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-4 w-4 flex-shrink-0" />
                  Active from: <span className="font-medium text-foreground">{new Date(activeFrom).toLocaleString()}</span>
                </p>
              )}
            </div>
          )}

          {(step === 'executing' || step === 'done') && isSimpleMode && (
            <div className="space-y-4 py-4">
              {step === 'executing' && (
                <>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-150"
                      style={{ width: `${systemInfos.length > 0 ? (simpleExecProgress / systemInfos.length) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">
                    Adding {simpleExecProgress} of {toAdd.length}
                    …
                  </p>
                </>
              )}
              {step === 'done' && (
                <div className="grid grid-cols-3 gap-2">
                  <SummaryCard
                    label="Added"
                    value={cumulative.done}
                    colorClass="text-green-600"
                    copyIds={!incognito.isIncognito ? cumulativeSuccesses : undefined}
                  />
                  <SummaryCard
                    label="Skipped"
                    value={cumulative.skipped}
                    colorClass="text-muted-foreground"
                    copyIds={!incognito.isIncognito ? cumulativeSkippedIDs : undefined}
                  />
                  <SummaryCard
                    label="Failed"
                    value={cumulative.error}
                    colorClass={cumulative.error > 0 ? 'text-destructive' : 'text-muted-foreground'}
                    copyIds={!incognito.isIncognito ? cumulativeFailures.map((f) => f.systemID) : undefined}
                  />
                </div>
              )}
              {step === 'done' && (
                <FailureBreakdown failures={cumulativeFailures} isIncognito={incognito.isIncognito} />
              )}
            </div>
          )}

          {(step === 'executing' || step === 'done') && !isSimpleMode && (
            <div className="space-y-2">
              {step === 'done' && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <SummaryCard
                    label="Added"
                    value={cumulative.done}
                    colorClass="text-green-600"
                    copyIds={!incognito.isIncognito ? cumulativeSuccesses : undefined}
                  />
                  <SummaryCard
                    label="Skipped"
                    value={cumulative.skipped}
                    colorClass="text-muted-foreground"
                    copyIds={!incognito.isIncognito ? cumulativeSkippedIDs : undefined}
                  />
                  <SummaryCard
                    label="Failed"
                    value={cumulative.error}
                    colorClass={cumulative.error > 0 ? 'text-destructive' : 'text-muted-foreground'}
                    copyIds={!incognito.isIncognito ? cumulativeFailures.map((f) => f.systemID) : undefined}
                  />
                </div>
              )}
              {step === 'executing' && (
                <div className="space-y-4 py-4">
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-150"
                      style={{ width: `${systemInfos.length > 0 ? (detailExecProgress / systemInfos.length) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">
                    Processing {detailExecProgress} of {systemInfos.length}…
                  </p>
                </div>
              )}
              {step === 'done' && (
                <FailureBreakdown failures={cumulativeFailures} isIncognito={incognito.isIncognito} />
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-2">
          {step === 'import' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={() => startChecking()} disabled={parsedIDs.length === 0}>
                Check Systems
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 'checking' && (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('import')}>
                Back
              </Button>
              <Button
                onClick={() => {
                  if (otherPoolBehavior === 'remove' && systemInfos.some((s) => !s.alreadyInTargetPool && s.otherPools.some(isControlPoolId))) {
                    setShowDeleteConfirm(true)
                  } else {
                    startExecuting()
                  }
                }}
                disabled={toAdd.length === 0}
              >
                {toAdd.length === 0
                  ? 'Nothing to do'
                  : `Proceed (${toAdd.length} system${toAdd.length !== 1 ? 's' : ''})`}
              </Button>
            </>
          )}
          {step === 'executing' && (
            <Button variant="outline" disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing…
            </Button>
          )}
          {step === 'done' && (
            <Button onClick={handleClose}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={showDeleteConfirm} onOpenChange={(o) => { if (!o) { setShowDeleteConfirm(false); setDeleteConfirmText('') } }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Confirm removal</DialogTitle>
          <DialogDescription>
            Are you sure you want to remove these systems from their old control pool?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Type <strong className="text-foreground">DELETE</strong> to confirm.
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText('') }}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteConfirmText !== 'DELETE'}
            onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); startExecuting() }}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
